import { defer } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import type { TFunction } from "i18next"
import { LucideIcon } from "lucide-react"
import { ComponentProps, MouseEvent, ReactNode } from "react"

export type Action =
  | ({
      id: string
      title?: string
      icon: LucideIcon
      variant?: ComponentProps<typeof Button>["variant"]
      params?: Record<string, string>
      search?: Record<string, string>
    } & (
      | {
          to: string
        }
      | {
          onClick: (e: MouseEvent) => void | (() => Promise<void>)
        }
    ))
  | {
      id: string
      component: ReactNode
    }

type Path = string
type Title = string | ((t: TFunction) => string | Promise<string>) | Promise<string>
type Actions = Action[] | ((t: TFunction) => Action[]) | Promise<Action[]>

type CrumbConstructor = {
  fullPath: Path
  title?: Title
  replace?: boolean
  actions?: Actions
  noPadding?: boolean
}

export class Crumb {
  fullPath: Path
  title?: Title
  actions?: Actions
  replace?: boolean
  noPadding?: boolean

  constructor({ fullPath, title, actions, replace, noPadding }: CrumbConstructor) {
    this.fullPath = fullPath
    this.title = title
    this.actions = actions
    this.replace = replace
    this.noPadding = noPadding
  }

  getTitle(t: TFunction) {
    const title = typeof this.title === "function" ? this.title(t) : this.title

    if (!title) {
      return undefined
    }

    if (typeof title === "string") {
      return { kind: "string", value: title } as const
    }

    if (title instanceof Promise) {
      return { kind: "defer", value: defer(title) } as const
    }

    if (typeof title === "object") {
      if (location.hash === "#dev-lang") {
        return { kind: "string", value: title } as const
      }

      return { kind: "defer", value: defer(title as Promise<string>) } as const
    }

    throw new Error("Invalid title", { cause: title })
  }

  getActions(t: TFunction) {
    if (Array.isArray(this.actions)) {
      return { kind: "array", value: this.actions } as const
    }

    if (this.actions instanceof Promise) {
      return { kind: "defer", value: defer(this.actions) } as const
    }

    if (typeof this.actions === "function") {
      return { kind: "array", value: this.actions(t) } as const
    }

    if (!this.actions) {
      return undefined
    }

    throw new Error("Invalid actions", { cause: this.actions })
  }

  isReplace() {
    return !!this.replace
  }
}
